import React from 'react';

export const WITHDRAWAL_FAIL_REASONS = [
  'The bank account you provided appears to be inactive or closed. Please verify your account is active and try again.',
  'The transfer was declined by your bank. Please contact your bank or use a different payout method.',
  'Your payout details could not be verified. Please update your payment method and resubmit.',
  'A processing error occurred on our end. No tokens were deducted and your balance remains unchanged.',
  'Your account was flagged during a routine security review. Please contact support.',
];

export const CASHOUT_REJECT_REASONS = [
  'Your bank account name does not match the name on your verified identity. Please update your bank details with matching information.',
  'Your account does not currently meet the cashout eligibility requirements. Please review the requirements and try again.',
  'Unusual activity was detected on your account. This has triggered a standard fraud prevention review.',
  'Additional identity verification is required before we can process your cashout request. Please contact support.',
  'Your request was denied due to a policy violation on your account.',
];

type WithdrawalStatus = 'processing' | 'completed' | 'failed' | 'cancelled';

interface Props {
  status: WithdrawalStatus;
  failureReason?: string | null;
  /** ISO date string — shown as "Attempted" for failed, "Rejected on" for cancelled */
  processedAt?: string | null;
  /** ISO date string — shown as "Est. release" for processing */
  estimatedArrivalTo?: string | null;
  grossUsd?: number;
  referenceId?: string;
  createdAt?: string | null;
}

const fmt = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className='flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0'>
    <span className='text-xs text-gray-500'>{label}</span>
    <span className='text-xs font-semibold text-gray-800'>{value}</span>
  </div>
);

const WithdrawalStatusMessage: React.FC<Props> = ({
  status,
  failureReason,
  processedAt,
  estimatedArrivalTo,
  grossUsd,
  referenceId,
  createdAt,
}) => {
  if (status === 'completed') return null;

  if (status === 'failed') {
    return (
      <div className='rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-3'>
        <div className='flex items-center gap-2'>
          <span className='flex h-7 w-7 items-center justify-center rounded-full bg-rose-100'>
            <svg className='h-4 w-4 text-rose-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </span>
          <span className='text-sm font-bold text-gray-900'>Transfer Failed</span>
          <span className='ml-auto inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-600'>Failed</span>
        </div>

        {grossUsd !== undefined && (
          <p className='text-xs text-gray-500'>
            We couldn't complete your{' '}
            <span className='font-semibold text-gray-700'>${grossUsd.toFixed(2)}</span> cash out due to a technical issue. No tokens were deducted and your balance remains unchanged.
          </p>
        )}

        {failureReason && (
          <div className='rounded-lg border-l-4 border-rose-500 bg-white px-3 py-2.5'>
            <p className='mb-1 text-xs font-semibold text-rose-600'>What went wrong</p>
            <p className='text-xs text-gray-700 leading-relaxed'>{failureReason}</p>
          </div>
        )}

        <div className='rounded-lg border border-gray-200 bg-white px-3 py-1'>
          {referenceId && <DetailRow label='Reference ID' value={`#${referenceId}`} />}
          {createdAt && <DetailRow label='Initiated' value={fmt(createdAt) || '—'} />}
          {processedAt && <DetailRow label='Attempted' value={fmt(processedAt) || '—'} />}
          <DetailRow label='Status' value='Failed' />
        </div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className='rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-3'>
        <div className='flex items-center gap-2'>
          <span className='flex h-7 w-7 items-center justify-center rounded-full bg-rose-100'>
            <svg className='h-4 w-4 text-rose-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </span>
          <span className='text-sm font-bold text-gray-900'>Cash Out Request Denied</span>
          <span className='ml-auto inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-600'>Rejected</span>
        </div>

        {failureReason && (
          <div className='rounded-lg border-l-4 border-rose-500 bg-white px-3 py-2.5'>
            <p className='mb-1 text-xs font-semibold text-rose-600'>Reason for rejection</p>
            <p className='text-xs text-gray-700 leading-relaxed'>{failureReason}</p>
          </div>
        )}

        <div className='rounded-lg border border-gray-200 bg-white px-3 py-1'>
          {referenceId && <DetailRow label='Reference ID' value={`#${referenceId}`} />}
          {createdAt && <DetailRow label='Initiated' value={fmt(createdAt) || '—'} />}
          {processedAt && <DetailRow label='Rejected on' value={fmt(processedAt) || '—'} />}
          <DetailRow label='Status' value='Rejected' />
        </div>
      </div>
    );
  }

  if (status === 'processing') {
    return (
      <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3'>
        <div className='flex items-center gap-2'>
          <span className='flex h-7 w-7 items-center justify-center rounded-full bg-amber-100'>
            <svg className='h-4 w-4 text-amber-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </span>
          <span className='text-sm font-bold text-gray-900'>Cash Out Under Review</span>
          <span className='ml-auto inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-600'>In progress</span>
        </div>

        {grossUsd !== undefined && (
          <p className='text-xs text-gray-500'>
            Your{' '}
            <span className='font-semibold text-gray-700'>${grossUsd.toFixed(2)}</span> cash out is currently on hold while our team completes a routine compliance check. This is not a rejection.
          </p>
        )}

        {failureReason && (
          <div className='rounded-lg border-l-4 border-amber-400 bg-white px-3 py-2.5'>
            <p className='mb-1 text-xs font-semibold text-amber-600'>Why it's on hold</p>
            <p className='text-xs text-gray-700 leading-relaxed'>{failureReason}</p>
          </div>
        )}

        <div className='rounded-lg border border-gray-200 bg-white px-3 py-1'>
          {referenceId && <DetailRow label='Reference ID' value={`#${referenceId}`} />}
          {createdAt && <DetailRow label='Initiated' value={fmt(createdAt) || '—'} />}
          {estimatedArrivalTo && <DetailRow label='Est. release' value={fmt(estimatedArrivalTo) || '—'} />}
          <DetailRow label='Status' value='In progress' />
        </div>
      </div>
    );
  }

  return null;
};

export default WithdrawalStatusMessage;
